<template>
  <div class="min-h-screen bg-[#f8fafc] font-sans">
    
    <!-- Top Navigation Bar -->
    <nav class="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div class="flex items-center gap-3 cursor-pointer" @click="$router.push('/')">
          <img src="@/assets/images/aboutUs/gbicon.svg" alt="GetBoarded" class="w-8 h-8 object-contain" />
          <span class="font-bold text-xl text-gray-900 tracking-tight">GetBoarded</span>
        </div>
        <vs-button type="border" color="#eab308" class="font-bold rounded-full px-6 shadow-sm" @click="$router.push('/')">
          Create Profile
        </vs-button>
      </div>
    </nav>

    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      
      <!-- LOADING & ERROR STATES -->
      <div v-if="loading" class="flex flex-col justify-center items-center h-64 gap-4">
        <feather-icon icon="LoaderIcon" class="w-10 h-10 text-primary animate-spin" />
        <span class="text-gray-500 font-semibold tracking-wide">Loading profile...</span>
      </div>

      <div v-else-if="error" class="flex justify-center items-center h-64">
        <div class="text-center bg-white p-8 rounded-2xl shadow-sm border border-gray-200 max-w-md w-full">
          <feather-icon icon="LockIcon" class="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 class="text-xl font-bold text-gray-800 mb-2">Profile Unavailable</h2>
          <p class="text-gray-500 mb-6">{{ error }}</p>
        </div>
      </div>

      <!-- MAIN PROFILE VIEW -->
      <div v-else-if="orgData" class="space-y-8">
        
        <!-- 1. HERO IDENTITY HEADER -->
        <div class="relative overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-sm">

          <!-- Soft decorative background -->
          <div class="absolute rounded-full pointer-events-none" style="width: 180px; height: 180px; top: -80px; right: -50px; background: rgba(240, 191, 70, 0.08);"></div>
          <div class="absolute rounded-full pointer-events-none" style="width: 140px; height: 140px; bottom: -80px; left: -40px; background: rgba(240, 191, 70, 0.06);"></div>

          <!-- MAIN CONTENT -->
          <div class="relative z-10 flex flex-col lg:flex-row items-center" style="padding: 32px;">

            <!-- LOGO -->
            <div class="flex-shrink-0 mr-0 lg:mr-12 mb-5 lg:mb-0">
              <div
                style="width: 108px; height: 108px; transition: all 0.25s ease;"
                @mouseenter="$event.currentTarget.style.transform='translateY(-3px) scale(1.03)'"
                @mouseleave="$event.currentTarget.style.transform='translateY(0) scale(1)'"
              >
                <vs-avatar
                  v-if="orgData.logo"
                  class="m-0 !w-full !h-full overflow-hidden"
                  size="108px"
                  :src="constants.s3Bucket + '/organization/logo/' + orgData.logo"
                />
                <div
                  v-else
                  class="w-full h-full flex items-center justify-center rounded-xl font-bold text-yellow-600"
                  style="font-size: 38px; background: linear-gradient(135deg, #fff8df, #fff1bd);"
                >
                  {{ (orgData.name || orgData.companyName).charAt(0).toUpperCase() }}
                </div>
              </div>
            </div>

            <!-- COMPANY INFO -->
            <div class="flex-1 min-w-0 w-full lg:w-auto text-center lg:text-left">
              <h1 class="font-bold text-gray-900" style="font-size: 30px; line-height: 1.2; margin: 0 0 14px 0; letter-spacing: -0.5px;">
                {{ orgData.name || orgData.companyName }}
              </h1>

              <!-- META -->
              <div class="flex flex-wrap items-center justify-center lg:justify-start" style="gap: 8px; margin-bottom: 18px;">
                <span v-if="orgData.organizationType" class="inline-flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700" style="padding: 7px 11px; font-size: 13px; transition: all 0.2s ease;">
                  <feather-icon icon="BriefcaseIcon" style="width: 15px; height: 15px;" />
                  {{ orgData.organizationType }}
                </span>

                <span v-if="orgData.location" class="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg text-blue-700" style="padding: 7px 11px; font-size: 13px;">
                  <feather-icon icon="MapPinIcon" style="width: 15px; height: 15px;" />
                  {{ orgData.location }}
                </span>

                <a v-if="orgData.website" :href="formatUrl(orgData.website)" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg text-blue-600" style="padding: 7px 11px; font-size: 13px; font-weight: 600; text-decoration: none; transition: all 0.2s ease;" @mouseenter="$event.currentTarget.style.background='#dbeafe'" @mouseleave="$event.currentTarget.style.background='#eff6ff'">
                  <feather-icon icon="GlobeIcon" style="width: 15px; height: 15px;" />
                  <span>Company Website</span>
                  <feather-icon icon="ExternalLinkIcon" style="width: 12px; height: 12px;" />
                </a>
              </div>

              <!-- STATUS -->
              <div class="flex flex-wrap items-center justify-center lg:justify-start" style="gap: 8px;">
                <span v-if="openRolesCount > 0 || (orgData.openRoles && orgData.openRoles.length > 0)" class="inline-flex items-center gap-2 bg-green-50 border border-green-100 rounded-full text-green-700 font-semibold" style="padding: 7px 12px; font-size: 12px;">
                  <span class="rounded-full bg-green-500" style="width: 7px; height: 7px;"></span>
                  Open To Hiring
                </span>
                <span class="inline-flex items-center gap-2 bg-purple-50 border border-purple-100 rounded-full text-purple-700 font-semibold" style="padding: 7px 12px; font-size: 12px;">
                  <span class="rounded-full bg-purple-500" style="width: 7px; height: 7px;"></span>
                  Open To Partnerships
                </span>
              </div>
            </div>

            <!-- ACTION BUTTONS -->
            <div class="flex-shrink-0 w-full lg:w-auto mt-6 lg:mt-0 lg:ml-8" style="border-top: 1px solid #f1f1f1; padding-top: 20px;">
              <div class="flex flex-col sm:flex-row lg:flex-col" style="gap: 10px; min-width: 230px;">
                <a v-if="orgData.website" :href="formatUrl(orgData.website)" target="_blank" rel="noopener noreferrer" class="inline-flex items-center justify-center rounded-xl font-semibold" style="height: 46px; padding: 0 18px; background: #F0BF46; color: #111827; text-decoration: none; font-size: 14px; transition: all 0.25s ease; box-shadow: 0 3px 10px rgba(240, 191, 70, 0.15);" @mouseenter="$event.currentTarget.style.transform='translateY(-2px)'; $event.currentTarget.style.background='#eab535'; $event.currentTarget.style.boxShadow='0 7px 18px rgba(240,191,70,0.28)'" @mouseleave="$event.currentTarget.style.transform='translateY(0)'; $event.currentTarget.style.background='#F0BF46'; $event.currentTarget.style.boxShadow='0 3px 10px rgba(240,191,70,0.15)'">
                  <feather-icon icon="ExternalLinkIcon" class="mr-2" style="width: 17px; height: 17px;" />
                  <span>Visit Website</span>
                  <feather-icon icon="ArrowRightIcon" class="ml-auto" style="width: 15px; height: 15px;" />
                </a>

                <a :href="orgData.workEmail ? `mailto:${orgData.workEmail}` : '#'" class="inline-flex items-center justify-center rounded-xl font-semibold bg-white border border-gray-300 text-gray-800" style="height: 46px; padding: 0 18px; font-size: 14px; text-decoration: none; transition: all 0.25s ease;" @mouseenter="$event.currentTarget.style.transform='translateY(-2px)'; $event.currentTarget.style.borderColor='#9ca3af'; $event.currentTarget.style.boxShadow='0 5px 15px rgba(0,0,0,0.07)'" @mouseleave="$event.currentTarget.style.transform='translateY(0)'; $event.currentTarget.style.borderColor='#d1d5db'; $event.currentTarget.style.boxShadow='none'">
                  <feather-icon icon="MailIcon" class="mr-2 text-gray-500" style="width: 17px; height: 17px;" />
                  <span>Contact Us</span>
                  <feather-icon icon="ArrowRightIcon" class="ml-auto text-gray-400" style="width: 15px; height: 15px;" />
                </a>
              </div>
            </div>

          </div>
        </div>

        <!-- 2. CENTERED TAB NAVIGATION -->
        <div class="w-full overflow-x-auto hide-scrollbar">
          <div class="flex w-max mx-auto items-center gap-2" style="padding: 2px 0;">
            <button
              v-for="tab in ['Overview', 'People & Culture', 'Opportunities', 'Resources']"
              :key="tab"
              @click="activeTab = tab"
              class="inline-flex items-center justify-center whitespace-nowrap rounded-full font-semibold border outline-none appearance-none transition-all duration-200"
              :class="activeTab === tab ? 'bg-yellow-700 text-black border-yellow-400 shadow-sm' : 'bg-white text-gray-700 border-gray-400 hover:bg-gray-50 hover:border-gray-500 hover:-translate-y-0.5'"
              style="height: 38px; padding: 0 19px; font-size: 13px;"
            >
              {{ tab }}
            </button>
          </div>
        </div>

        <!-- 3. TWO-COLUMN CONTENT -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

          <!-- LEFT COLUMN -->
          <div class="lg:col-span-2 space-y-6">

            <!-- ABOUT -->
            <div v-show="activeTab === 'Overview'" class="animate-fade-in">
              <div v-if="orgData.about" class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
                <h2 class="text-[19px] font-extrabold text-gray-900 mb-4">About</h2>
                <div class="text-gray-600 leading-relaxed whitespace-pre-line text-lg">
                  {{ orgData.about }}
                </div>
              </div>
            </div>

            <!-- PEOPLE & CULTURE -->
            <div v-show="activeTab === 'Overview' || activeTab === 'People & Culture'" class="animate-fade-in">
              <div v-if="orgData.leadership && orgData.leadership.length > 0" class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
                <h2 class="text-[19px] font-extrabold text-gray-900 mb-5">Leadership & team</h2>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div v-for="(leader, idx) in orgData.leadership" :key="idx" class="flex items-center gap-4 p-4 border border-gray-100 rounded-xl bg-gray-50/80">
                    <vs-avatar size="50px" :src="getAvatarUrl(leader)" class="m-0 shadow-sm border border-gray-200" />
                    <div>
                      <p class="font-bold text-left text-gray-900 text-[15px]">{{ leader.firstName }} {{ leader.lastName }}</p>
                      <p class="text-[11px] text-left text-gray-500 font-bold uppercase tracking-wider mt-0.5">{{ leader.role }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- OPPORTUNITIES -->
            <div v-show="activeTab === 'Overview' || activeTab === 'Opportunities'" class="animate-fade-in">
              <div v-if="orgData.openRoles && orgData.openRoles.length > 0">
                <h2 v-if="activeTab === 'Overview'" class="text-[19px] font-extrabold text-gray-900 mb-5 mt-2">Open opportunities</h2>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div v-for="(role, idx) in paginatedRoles" :key="idx" class="border border-gray-200 rounded-2xl p-6 bg-white hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 flex flex-col group">
                    <div class="inline-block px-2.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[11px] font-bold uppercase tracking-wider w-max mb-3">Open Role</div>
                    <h3 class="font-bold text-[18px] text-gray-900 mb-2 group-hover:text-yellow-600 transition-colors">{{ role.title }}</h3>
                    <p v-if="role.skills && role.skills.length" class="text-[13px] text-gray-500 mb-5 leading-relaxed line-clamp-2" style="text-transform: capitalize;">
                      {{ role.skills.join(', ') }}
                    </p>
                    <p v-else class="text-[13px] text-gray-400 italic mb-5">To be discussed during the interview process.</p>
                    <div class="flex items-center gap-2 text-[13px] text-gray-500 font-medium mt-auto">
                      <feather-icon icon="MapPinIcon" class="w-4 h-4 text-gray-400" />
                      <span>{{ orgData.location || 'Remote' }}</span>
                    </div>
                  </div>
                </div>
                <!-- Pagination -->
                <div v-if="totalRolePages > 1" class="mt-8 flex justify-center border-t border-gray-100 pt-6">
                  <vs-pagination :total="totalRolePages" v-model="rolesCurrentPage" color="#eab308" />
                </div>
              </div>
              <div v-else-if="activeTab === 'Opportunities'" class="text-center py-16 bg-white rounded-2xl border border-gray-200">
                <feather-icon icon="BriefcaseIcon" class="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p class="text-gray-500 font-medium">No open roles currently available.</p>
              </div>
            </div>

            <!-- RESOURCES & DOCUMENTS -->
            <div v-show="activeTab === 'Overview' || activeTab === 'Resources'" class="animate-fade-in space-y-6">
              
              <!-- SECTION 1: Attached Documents -->
              <div v-if="attachedDocumentsList.length > 0" class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
                <h2 class="text-[19px] font-extrabold text-gray-900 mb-5">Attached documents</h2>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div
                    v-for="(doc, idx) in (activeTab === 'Overview' ? attachedDocumentsList.slice(0, 2) : attachedDocumentsList)"
                    :key="'res-doc-' + idx"
                    class="group flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-4 transition-all duration-200 hover:border-gray-300 hover:shadow-sm"
                  >
                    <!-- LEFT: FILE INFO -->
                    <div class="flex min-w-0 items-center gap-3">
                      <div :class="['flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', getFileBgColor(doc.file_extension)]">
                        <feather-icon :icon="getFileIcon(doc.file_extension)" :class="['w-5 h-5', getFileIconColor(doc.file_extension)]" />
                      </div>
                      <div class="min-w-0 overflow-hidden">
                        <p class="truncate text-[14px] font-bold text-gray-900" :title="doc.original_filename">{{ doc.original_filename }}</p>
                        <p class="mt-1 text-[11px] font-medium text-left uppercase tracking-wide text-gray-400">{{ doc.file_extension }} • {{ doc.file_size }}</p>
                      </div>
                    </div>

                    <!-- RIGHT: CLEAN ICON ACTIONS -->
                    <div class="ml-2 flex shrink-0 items-center gap-1">
                      
                      <!-- Preview -->
                      <div class="relative group/action">
                        <button type="button" @click.stop="previewDocument(doc)" class="bg-transparent border-none p-2 flex items-center justify-center text-gray-400 hover:text-blue-500 outline-none transition-colors duration-200 cursor-pointer">
                          <feather-icon icon="EyeIcon" class="w-5 h-5" />
                        </button>
                        <span class="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-[11px] font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/action:opacity-100">Preview</span>
                      </div>

                      <!-- Download -->
                      <div class="relative group/action">
                        <button type="button" @click.stop="downloadFile(doc.original_file_url, doc.original_filename)" class="bg-transparent border-none p-2 flex items-center justify-center text-gray-400 hover:text-green-500 outline-none transition-colors duration-200 cursor-pointer">
                          <feather-icon icon="DownloadIcon" class="w-5 h-5" />
                        </button>
                        <span class="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-[11px] font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/action:opacity-100">Download</span>
                      </div>

                    </div>
                  </div>
                </div>
              </div>

              <!-- SECTION 2: Media/Featured Resources -->
              <div v-if="mediaResourcesList.length > 0 && (activeTab === 'Resources' || (activeTab === 'Overview' && attachedDocumentsList.length === 0))" class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
                <h2 class="text-[19px] font-extrabold text-gray-900 mb-5">Featured resources</h2>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div
                    v-for="(doc, idx) in (activeTab === 'Overview' ? mediaResourcesList.slice(0, 2) : mediaResourcesList)"
                    :key="'res-media-' + idx"
                    class="group flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-4 transition-all duration-200 hover:border-gray-300 hover:shadow-sm"
                  >
                    <!-- LEFT: FILE INFO -->
                    <div class="flex min-w-0 items-center gap-3">
                      <div :class="['flex h-11 w-11 shrink-0 items-center justify-center rounded-xl', getFileBgColor(doc.file_extension)]">
                        <feather-icon :icon="getFileIcon(doc.file_extension)" :class="['w-5 h-5', getFileIconColor(doc.file_extension)]" />
                      </div>
                      <div class="min-w-0 overflow-hidden">
                        <p class="truncate text-[14px] font-bold text-gray-900" :title="doc.original_filename">{{ doc.original_filename }}</p>
                        <p class="mt-1 text-[11px] font-medium text-left uppercase tracking-wide text-gray-400">{{ doc.file_extension }} • {{ doc.file_size }}</p>
                      </div>
                    </div>

                    <!-- RIGHT: ACTIONS -->
                    <div class="ml-2 flex shrink-0 items-center gap-1">
                      
                      <!-- Preview (Only show for images) -->
                      <div class="relative group/action" v-if="isImageFile(doc.file_extension)">
                        <button type="button" @click.stop="previewDocument(doc)" class="bg-transparent border-none p-2 flex items-center justify-center text-gray-400 hover:text-blue-500 outline-none transition-colors duration-200 cursor-pointer">
                          <feather-icon icon="EyeIcon" class="w-5 h-5" />
                        </button>
                        <span class="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-[11px] font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/action:opacity-100">Preview</span>
                      </div>

                      <!-- Download -->
                      <div class="relative group/action">
                        <button type="button" @click.stop="downloadFile(doc.original_file_url, doc.original_filename)" class="bg-transparent border-none p-2 flex items-center justify-center text-gray-400 hover:text-green-500 outline-none transition-colors duration-200 cursor-pointer">
                          <feather-icon icon="DownloadIcon" class="w-5 h-5" />
                        </button>
                        <span class="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-[11px] font-medium text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover/action:opacity-100">Download</span>
                      </div>
                      
                    </div>
                  </div>
                </div>
              </div>

              <!-- EMPTY STATE FOR RESOURCES -->
              <div v-if="publicDocuments.length === 0 && activeTab === 'Resources'" class="text-center py-16 bg-white rounded-2xl border border-gray-200">
                <feather-icon icon="FileTextIcon" class="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p class="text-gray-500 font-medium">No public resources have been shared yet.</p>
              </div>
            </div>

          </div>

          <!-- RIGHT SIDEBAR -->
          <div class="space-y-6 sticky top-24">
            
            <!-- AT A GLANCE -->
            <div class="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              
              <!-- HEADER -->
              <div class="mb-6">
                <h3 class="text-[17px] font-extrabold text-gray-900">At a glance</h3>
                <p class="text-[13px] text-gray-500 mt-1">A quick view of the ecosystem</p>
              </div>

              <!-- DETAILS -->
              <div class="space-y-6">
                
                <!-- Organization Type -->
                <div v-if="orgData.organizationType" class="flex items-start gap-4 min-w-0">
                  <feather-icon icon="BriefcaseIcon" class="text-blue-400 mt-0.5 w-5 h-5 shrink-0" />
                  <div class="min-w-0">
                    <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500 block">Organization type</span>
                    <span class="text-[14px] font-semibold text-gray-800 block mt-0.5">{{ orgData.organizationType }}</span>
                  </div>
                </div>

                <!-- Talent Community & Evaluated Roles -->
                <div v-if="orgData.employeeCount !== undefined" class="flex items-start gap-4 min-w-0">
                  <feather-icon icon="UsersIcon" class="text-blue-500 mt-0.5 w-5 h-5 shrink-0" />
                  <div class="min-w-0">
                    <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500 block">Talent community</span>
                    <span class="text-[14px] font-semibold text-gray-800 block mt-0.5">{{ orgData.employeeCount }} Members</span>
                    <!-- FIX: Evaluated Badge -->
                    <span v-if="evaluatedMembersCount > 0" class="text-[12px] text-blue-600 font-medium block mt-1 bg-blue-50 px-2 py-0.5 rounded inline-block">
                      <span class="font-bold">{{ evaluatedMembersCount }}</span> in evaluated roles
                    </span>
                  </div>
                </div>

                <!-- Key Roles & Open Hiring -->
                <div v-if="orgData.totalRoles !== undefined" class="flex items-start gap-4 min-w-0">
                  <feather-icon icon="LayersIcon" class="text-purple-600 mt-0.5 w-5 h-5 shrink-0" />
                  <div class="min-w-0">
                    <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500 block">Key Roles</span>
                    <span class="text-[14px] font-semibold text-gray-800 block mt-0.5">{{ orgData.totalRoles }} Total Roles</span>
                    <!-- FIX: Use orgData.openRoles.length to guarantee it matches the UI -->
                    <span v-if="orgData.openRoles && orgData.openRoles.length > 0" class="text-[12px] text-purple-700 font-medium block mt-1 bg-purple-50 px-2 py-0.5 rounded inline-block">
                      <span class="font-bold">{{ orgData.openRoles.length }}</span> open for hiring
                    </span>
                  </div>
                </div>

                <!-- Headquarters -->
                <div v-if="orgData.location" class="flex items-start gap-4 min-w-0">
                  <feather-icon icon="MapPinIcon" class="text-yellow-500 mt-0.5 w-5 h-5 shrink-0" />
                  <div class="min-w-0">
                    <span class="text-[11px] font-bold uppercase tracking-wider text-gray-500 block">Headquarters</span>
                    <span class="text-[14px] font-semibold text-gray-800 block mt-0.5 truncate whitespace-normal">{{ orgData.location }}</span>
                  </div>
                </div>

              </div>
            </div>

            <!-- ATTACHED DOCUMENTS (SIDEBAR) -->
            <div v-if="sidebarDocuments.length > 0" class="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h3 class="text-[15px] font-extrabold text-gray-900 mb-4 flex items-center gap-2">
                <feather-icon icon="FileTextIcon" class="w-4 h-4 text-orange-500" /> Attached Documents
              </h3>
              <div class="space-y-3">
                <div
                  v-for="(doc, idx) in sidebarDocuments"
                  :key="'side-doc-' + idx"
                  class="flex items-center justify-between p-3 rounded-lg border border-gray-100 bg-gray-50 hover:bg-white hover:border-gray-300 transition-colors group cursor-pointer"
                  @click="previewDocument(doc)"
                >
                  <div class="flex items-center gap-3 min-w-0">
                    <feather-icon :icon="getFileIcon(doc.file_extension)" :class="['w-5 h-5 shrink-0', getFileIconColor(doc.file_extension)]" />
                    <div class="min-w-0">
                      <p class="text-[13px] font-bold text-gray-800 truncate" :title="doc.original_filename">{{ doc.original_filename }}</p>
                      <p class="text-[10px] text-gray-500 font-semibold text-left uppercase">{{ doc.file_extension }} • {{ doc.file_size }}</p>
                    </div>
                  </div>
                  <feather-icon icon="EyeIcon" class="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors shrink-0 ml-2" />
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </main>

    <!-- MEGA PDF/IMAGE PREVIEW MODAL -->
    <vs-popup title="Document Preview" :active.sync="previewActive" class="mega-preview-modal">
      <div v-if="previewActive" class="w-full flex flex-col items-center justify-center bg-gray-100" style="height: 600px; max-height: 80vh;">
        
        <!-- Render Image -->
        <img 
          v-if="previewIsImage" 
          :src="currentPreviewUrl" 
          class="max-w-full max-h-full object-contain p-4"
          alt="Preview"
        />

        <!-- Render PDF in Iframe -->
        <iframe 
          v-else
          :src="currentPreviewUrl + '#toolbar=0&navpanes=0&scrollbar=0'" 
          class="flex-grow w-full border-0 bg-gray-100 rounded-b-lg"
          frameborder="0"
        ></iframe>
        
      </div>
    </vs-popup>

  </div>
</template>

<script>
import constants from "@/constants.js";

export default {
  name: "PublicOrganizationProfile",
  data() {
    return {
      constants,
      loading: true,
      error: null,
      orgData: null,
      
      activeTab: 'Overview', 
      
      rolesCurrentPage: 1,
      rolesPerPage: 6,

      // KVDR dynamic metrics
      evaluatedMembersCount: 0,
      openRolesCount: 0,

      // Document specific data
      publicDocuments: [],
      documentTypes: ['pdf', 'ppt', 'pptx', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt'],
      previewActive: false,
      previewIsImage: false, // <-- ADD THIS
      currentPreviewUrl: "",
    };
  },
  computed: {
    paginatedRoles() {
      if (!this.orgData || !this.orgData.openRoles) return [];
      const start = (this.rolesCurrentPage - 1) * this.rolesPerPage;
      const end = start + this.rolesPerPage;
      return this.orgData.openRoles.slice(start, end);
    },
    totalRolePages() {
      if (!this.orgData || !this.orgData.openRoles) return 0;
      return Math.ceil(this.orgData.openRoles.length / this.rolesPerPage);
    },
    apiUrl() {
      const url = process.env.VUE_APP_API_URL || "http://127.0.0.1:8000";
      return url.endsWith('/') ? url.slice(0, -1) : url;
    },
    // Filtering Documents natively for attached list
    attachedDocumentsList() {
      return this.publicDocuments.filter(doc => 
        this.documentTypes.includes((doc.file_extension || '').toLowerCase())
      );
    },
    // Filtering everything else into media resources (images, videos, etc.)
    mediaResourcesList() {
      return this.publicDocuments.filter(doc => 
        !this.documentTypes.includes((doc.file_extension || '').toLowerCase())
      );
    },
    // Sidebar strict limitation to 3 valid docs
    sidebarDocuments() {
      return this.attachedDocumentsList.slice(0, 3);
    }
  },
  created() {
    this.fetchPublicProfile();
  },
  methods: {
    isImageFile(ext) {
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes((ext || '').toLowerCase());
    },
    async fetchPublicProfile() {
      this.loading = true;
      this.error = null;
      const slug = this.$route.params.slug; 

      try {
        const response = await fetch(`${this.apiUrl}/api/public/organization/${slug}`);
        const result = await response.json();

        if (response.ok && result.status === 'success') {
          this.orgData = result.data;
          
          // FIX: Add broader fallbacks to ensure we capture the ID properly from public data
          const actualOrgId = this.orgData._id || this.orgData.id || this.orgData.organizationId || this.orgData.orgId;
          
          await Promise.all([
            this.fetchPublicDocuments(actualOrgId),
            this.fetchKvdrStats(actualOrgId)
          ]);
          
        } else {
          this.error = result.detail || "Organization profile is private or does not exist.";
        }
      } catch (err) {
        this.error = err.message || "An error occurred while loading the profile.";
      } finally {
        this.loading = false;
      }
    },
    
    async fetchPublicDocuments(orgId) {
      if (!orgId) return;
      try {
        const docRes = await fetch(`${this.apiUrl}/api/v2/organization/${orgId}/documents?public_only=true`);
        const docResult = await docRes.json();
        if (docRes.ok && docResult.status === 'success') {
          this.publicDocuments = docResult.data || [];
        }
      } catch (e) {
        console.error("Failed to fetch public documents", e);
      }
    },

    async fetchKvdrStats(orgId) {
      if (!orgId) return;
      try {
        const kvdrRes = await fetch(`https://api.getboarded.tech/kvdr?orgId=${orgId}`);
        const kvdrData = await kvdrRes.json();
        
        let uniqueEvaluatedUsers = new Set();
        
        // 1. Get the main data array
        const dataArray = Array.isArray(kvdrData) ? kvdrData : (kvdrData.data || []);

        // 2. Loop through the main documents
        dataArray.forEach(kvdrDoc => {
          
          // 3. Loop through all the roles (CEO, CTO, Backend Dev, etc.)
          if (kvdrDoc.kvdrRoles && Array.isArray(kvdrDoc.kvdrRoles)) {
            kvdrDoc.kvdrRoles.forEach(role => {
              
              // 4. Loop through all evaluated profiles under that specific role
              if (role.kvdrRoleProfiles && Array.isArray(role.kvdrRoleProfiles)) {
                role.kvdrRoleProfiles.forEach(profile => {
                  
                  // 5. Finally, grab the userId!
                  if (profile.userId) {
                    uniqueEvaluatedUsers.add(profile.userId.toString());
                  }
                  
                });
              }
            });
          }
        });
        
        this.evaluatedMembersCount = uniqueEvaluatedUsers.size;
        
      } catch (e) {
        console.error("Failed to fetch KVDR stats", e);
      }
    },

    previewDocument(doc) {
      if (!doc) return;
      
      if (this.isImageFile(doc.file_extension)) {
        // It's an image, use original URL
        this.currentPreviewUrl = `${this.apiUrl}${doc.original_file_url}`;
        this.previewIsImage = true;
      } else {
        // It's a document, use PDF URL
        if (!doc.pdf_file_url) {
          this.$vs.notify({ title: 'Processing', text: 'Document is still being prepared. Try again in a few seconds.', color: 'warning' });
          return;
        }
        this.currentPreviewUrl = `${this.apiUrl}${doc.pdf_file_url}`;
        this.previewIsImage = false;
      }
      this.previewActive = true;
    },

    downloadFile(fileUrl, fileName) {
      if (!fileUrl) return;
      const link = document.createElement('a');
      link.href = `${this.apiUrl}${fileUrl}`;
      link.setAttribute('download', fileName);
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    formatUrl(url) {
      if (!url) return '#';
      if (!url.match(/^https?:\/\//i)) {
        return 'http://' + url;
      }
      return url;
    },
    
    getAvatarUrl(leader) {
      if (leader.profilePictureUrl) {
        if (leader.profilePictureUrl.startsWith('http')) return leader.profilePictureUrl;
        return `${this.constants.s3Bucket}/editor/profile-pictures/${leader.profilePictureUrl}`;
      }
      const name = encodeURIComponent(`${leader.firstName} ${leader.lastName}`);
      return `https://ui-avatars.com/api/?name=${name}&background=f1f5f9&color=475569&bold=true`;
    },

    // --- UI Helpers for File Types ---
    getFileIcon(ext) {
      const e = (ext || '').toLowerCase();
      switch (e) {
        case 'pdf': return 'FileTextIcon';
        case 'ppt': 
        case 'pptx': return 'MonitorIcon';
        case 'doc': 
        case 'docx': return 'FileIcon';
        case 'xls': 
        case 'xlsx': 
        case 'csv': return 'GridIcon';
        case 'png':
        case 'jpg':
        case 'jpeg': return 'ImageIcon';
        case 'mp4':
        case 'avi':
        case 'mov': return 'VideoIcon';
        default: return 'FileIcon';
      }
    },
    getFileBgColor(ext) {
      const e = (ext || '').toLowerCase();
      switch (e) {
        case 'pdf': return 'bg-red-50';
        case 'ppt': 
        case 'pptx': return 'bg-orange-50';
        case 'doc': 
        case 'docx': return 'bg-blue-50';
        case 'xls': 
        case 'xlsx': 
        case 'csv': return 'bg-green-50';
        case 'png':
        case 'jpg':
        case 'jpeg': return 'bg-purple-50';
        case 'mp4':
        case 'avi':
        case 'mov': return 'bg-indigo-50';
        default: return 'bg-gray-100';
      }
    },
    getFileIconColor(ext) {
      const e = (ext || '').toLowerCase();
      switch (e) {
        case 'pdf': return 'text-red-600';
        case 'ppt': 
        case 'pptx': return 'text-orange-600';
        case 'doc': 
        case 'docx': return 'text-blue-600';
        case 'xls': 
        case 'xlsx': 
        case 'csv': return 'text-green-600';
        case 'png':
        case 'jpg':
        case 'jpeg': return 'text-purple-600';
        case 'mp4':
        case 'avi':
        case 'mov': return 'text-indigo-600';
        default: return 'text-gray-600';
      }
    }
  }
};
</script>

<style scoped>
/* Custom Scrollbar Hiding for tabs container on mobile */
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Smooth Fade In for Tab Content */
.animate-fade-in {
  animation: fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

/* =========================================================
   LARGE DOCUMENT PREVIEW
   ========================================================= */

/* Ensure the popup brings itself up with maximum space */
::v-deep .mega-preview-modal .vs-popup {
  width: 90vw !important;
  max-width: 800px !important; /* Changed from 1400px to 800px */
  height: auto !important; 
  margin: auto !important;
  display: flex !important;
  flex-direction: column !important;
  border-radius: 12px !important;
  overflow: hidden !important;
}

::v-deep .mega-preview-modal .vs-popup--content {
  width: 100% !important;
  height: 100% !important;
  flex-grow: 1 !important;
  padding: 0 !important;
  margin: 0 !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}
</style>